import { useMemo, useState, useEffect } from 'react';
import { Search, Download, Lock } from 'lucide-react';
import type { Ticket } from '@/lib/mock';
import { useUsers } from '@/lib/useUsers';
import { supabase } from '@/lib/supabase';

type Row = {
  at: number;
  ref: string;
  team: string;
  actor: string;
  kind: string;
  label: string;
  internal?: boolean;
};

function csvDownload(rows: Row[]) {
  const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
  const head = ['Time', 'Ref', 'Team', 'Actor', 'Kind', 'Detail'];
  const body = rows.map((r) => [new Date(r.at).toISOString(), r.ref, r.team, r.actor, r.kind, r.label].map(esc).join(','));
  const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'compass-audit-log.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function AuditPage({ tickets }: { tickets: Ticket[] }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [actor, setActor] = useState('all');
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const { profiles } = useUsers();

  useEffect(() => {
    if (!supabase) return;
    supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error }: { data: any; error: any }) => {
        if (!error && data) {
          setAuditLogs(data);
        }
      });
  }, []);

  const getActorName = (actorId: string | undefined) => {
    if (!actorId) return '—';
    if (actorId === '00000000-0000-0000-0000-000000000000') return 'System';
    return profiles.find(p => p.id === actorId)?.full_name || profiles.find(p => p.id === actorId)?.email || actorId;
  };

  const all = useMemo<Row[]>(
    () => {
      const ticketRows: Row[] = (tickets || []).flatMap((t) =>
        t.events.map((e) => ({
          at: e.at,
          ref: t.ref,
          team: t.team,
          actor: getActorName(e.actor),
          kind: e.kind,
          label: e.label,
          internal: e.internal,
        }))
      );

      const dbRows: Row[] = (auditLogs || []).map((log) => ({
        at: Date.parse(log.created_at) || Date.now(),
        ref: log.entity_id ? log.entity_id.substring(0, 8) : log.module || 'System',
        team: log.module || 'Administration',
        actor: getActorName(log.performed_by || log.user_id || log.actor_id),
        kind: log.action || 'system',
        label: log.description || log.action,
        internal: false,
      }));

      return [...ticketRows, ...dbRows].sort((a, b) => b.at - a.at);
    },
    [tickets, profiles, auditLogs],
  );

  const kinds = useMemo(() => ['all', ...new Set(all.map((r) => r.kind))], [all]);
  const actors = useMemo(() => ['all', ...new Set(all.map((r) => r.actor))], [all]);

  const rows = useMemo(
    () =>
      all.filter((r) => {
        if (kind !== 'all' && r.kind !== kind) return false;
        if (actor !== 'all' && r.actor !== actor) return false;
        if (q && !`${r.ref} ${r.label} ${r.actor} ${r.team}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      }),
    [all, kind, actor, q],
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1560px] px-6 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-medium tracking-tight">Audit log</h1>
            <p className="mt-1 text-sm text-muted-foreground">{rows.length} of {all.length} events · append-only</p>
          </div>
          <button
            onClick={() => csvDownload(rows)}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium transition hover:bg-muted"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {/* filters */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…" className="h-9 w-64 rounded-lg border border-input bg-background pl-8 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/30" />
          </div>
          <Select value={kind} onChange={setKind} options={kinds} label="kind" />
          <Select value={actor} onChange={setActor} options={actors} label="actor" />
        </div>

        {/* table */}
        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Time</th>
                <th className="px-4 py-2.5 font-medium">Ref</th>
                <th className="px-4 py-2.5 font-medium">Team</th>
                <th className="px-4 py-2.5 font-medium">Actor</th>
                <th className="px-4 py-2.5 font-medium">Kind</th>
                <th className="px-4 py-2.5 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 300).map((r, i) => (
                <tr key={i} className="border-b border-border/60 last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-[11px] text-muted-foreground">{new Date(r.at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-[11px] text-muted-foreground">{r.ref}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">{r.team}</td>
                  <td className="whitespace-nowrap px-4 py-2">{r.actor}</td>
                  <td className="px-4 py-2"><span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.kind}</span></td>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-1.5">
                      {r.internal && <Lock className="h-3 w-3 text-amber-500" />}
                      {r.label}
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">No events match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 300 && <p className="mt-2 text-center text-xs text-muted-foreground">Showing first 300 — refine filters or export CSV for the rest.</p>}
      </div>
    </div>
  );
}

function Select({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: string[]; label: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-lg border border-input bg-card px-2 text-sm outline-none focus:border-primary">
      {options.map((o) => <option key={o} value={o}>{o === 'all' ? `All ${label}s` : o}</option>)}
    </select>
  );
}
